'use client'

import { cn } from '@brew/ui/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@brew/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@brew/ui/select'
import type { ChatStatus } from 'ai'
import { Loader2Icon, PlusIcon, SquareIcon, XIcon } from 'lucide-react'
import { type ComponentProps, useCallback } from 'react'
import { BrewLogo } from '@/components/icons/brew-logo'
import { InputGroupButton } from '@/components/ui/input-group'

function BasePromptInputButton({
  variant = 'ghost',
  className,
  size,
  ...props
}: ComponentProps<typeof InputGroupButton>) {
  const newSize =
    size ?? ('children' in props && props.children ? 'sm' : 'icon-sm')

  return (
    <InputGroupButton
      className={cn(className)}
      size={newSize}
      type="button"
      variant={variant}
      {...props}
    />
  )
}

export type PromptInputActionMenuProps = ComponentProps<typeof DropdownMenu>
export const PromptInputActionMenu = (props: PromptInputActionMenuProps) => (
  <DropdownMenu {...props} />
)

export type PromptInputActionMenuTriggerProps = ComponentProps<
  typeof InputGroupButton
>

export const PromptInputActionMenuTrigger = ({
  className,
  children,
  ...props
}: PromptInputActionMenuTriggerProps) => (
  <DropdownMenuTrigger asChild>
    <BasePromptInputButton className={className} {...props}>
      {children ?? <PlusIcon className="size-4" />}
    </BasePromptInputButton>
  </DropdownMenuTrigger>
)

export type PromptInputActionMenuContentProps = ComponentProps<
  typeof DropdownMenuContent
>
export const PromptInputActionMenuContent = ({
  className,
  ...props
}: PromptInputActionMenuContentProps) => (
  <DropdownMenuContent align="start" className={cn(className)} {...props} />
)

export type PromptInputActionMenuItemProps = ComponentProps<
  typeof DropdownMenuItem
>
export const PromptInputActionMenuItem = ({
  className,
  ...props
}: PromptInputActionMenuItemProps) => (
  <DropdownMenuItem className={cn(className)} {...props} />
)

export type PromptInputSubmitProps = Omit<
  ComponentProps<typeof InputGroupButton>,
  'variant'
> & {
  status?: ChatStatus
  onStop?: () => void
}

export const PromptInputSubmit = ({
  className,
  status,
  children,
  disabled,
  onStop,
  ...props
}: PromptInputSubmitProps) => {
  const isStreaming = status === 'streaming'
  // A turn is stoppable from the instant it is submitted, not just once
  // tokens flow: the pre-first-token window (planning, tool set-up) is
  // long enough that it is exactly when people change their mind.
  const isStoppable =
    typeof onStop === 'function' && (isStreaming || status === 'submitted')

  let icon = <BrewLogo className="size-3.5 text-white" gap={50} />

  if (isStoppable) {
    icon = <SquareIcon className="size-4" />
  } else if (status === 'submitted') {
    icon = <Loader2Icon className="size-4 animate-spin" />
  } else if (isStreaming) {
    icon = <SquareIcon className="size-4" />
  } else if (status === 'error') {
    icon = <XIcon className="size-4" />
  }

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isStoppable && onStop) {
        event.preventDefault()
        onStop()
      }
    },
    [isStoppable, onStop]
  )

  return (
    <button
      aria-label={isStoppable || isStreaming ? 'Stop' : 'Submit'}
      // Never 'submit' while a run is in flight — without a handler the
      // ■ must be inert, not re-send the composer's contents.
      type={isStoppable || isStreaming ? 'button' : 'submit'}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg font-medium text-white transition-all duration-150',
        'bg-gradient-to-r from-orange-600 to-orange-500',
        'hover:from-orange-700 hover:to-orange-600',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:from-orange-600 disabled:hover:to-orange-500',
        className
      )}
      {...props}
    >
      {children ?? icon}
    </button>
  )
}

export type PromptInputModelSelectProps = ComponentProps<typeof Select>
export const PromptInputModelSelect = (props: PromptInputModelSelectProps) => (
  <Select {...props} />
)

export type PromptInputModelSelectTriggerProps = ComponentProps<
  typeof SelectTrigger
>
export const PromptInputModelSelectTrigger = ({
  className,
  ...props
}: PromptInputModelSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      'cursor-pointer border-none bg-transparent font-medium text-muted-foreground text-xs shadow-none transition-colors dark:bg-transparent',
      'hover:bg-transparent hover:text-foreground dark:hover:bg-transparent dark:hover:text-white/80 [&[aria-expanded="true"]]:bg-transparent [&[aria-expanded="true"]]:text-foreground dark:[&[aria-expanded="true"]]:text-white/80',
      '[&[aria-expanded="true"]_svg]:rotate-180 [&[aria-expanded="true"]_svg]:text-foreground [&[aria-expanded="true"]_svg]:opacity-100 dark:[&[aria-expanded="true"]_svg]:text-white/80 [&_svg]:transition-all [&_svg]:duration-150 hover:[&_svg]:text-foreground hover:[&_svg]:opacity-100 dark:hover:[&_svg]:text-white/80',
      className
    )}
    {...props}
  />
)

export type PromptInputModelSelectContentProps = ComponentProps<
  typeof SelectContent
>
export const PromptInputModelSelectContent = ({
  className,
  ...props
}: PromptInputModelSelectContentProps) => (
  <SelectContent
    className={cn(
      'min-w-[180px] rounded-lg border-none bg-foreground/[0.04] p-1 backdrop-blur-xl dark:bg-white/[0.05] [&_[data-radix-select-viewport]]:flex [&_[data-radix-select-viewport]]:flex-col [&_[data-radix-select-viewport]]:gap-1.5',
      className
    )}
    {...props}
  />
)

export type PromptInputModelSelectItemProps = ComponentProps<typeof SelectItem>
export const PromptInputModelSelectItem = ({
  className,
  ...props
}: PromptInputModelSelectItemProps) => (
  <SelectItem
    className={cn(
      'cursor-pointer rounded-lg p-1.5 font-normal text-xs transition-all duration-100',
      'text-muted-foreground focus:bg-foreground/[0.06] focus:text-foreground',
      'dark:text-white/40 dark:focus:bg-white/[0.07] dark:focus:text-white/85',
      'data-[state=checked]:bg-foreground/[0.06] data-[state=checked]:font-medium data-[state=checked]:text-foreground',
      'dark:data-[state=checked]:bg-white/[0.07] dark:data-[state=checked]:text-white/85',
      'hit-area hit-area-y-[3px] [&_svg]:!size-3.5 [&_svg]:!stroke-2',
      className
    )}
    {...props}
  />
)

export type PromptInputModelSelectValueProps = ComponentProps<
  typeof SelectValue
>
export const PromptInputModelSelectValue = (
  props: PromptInputModelSelectValueProps
) => <SelectValue {...props} />
